/**
 * Alarm patterns for the repeat-until-acknowledged new-alert alarm.
 *
 * Synthesized with the Web Audio API rather than shipped as audio files: no
 * assets, no network fetch at the moment an alert fires, and the patterns stay
 * legible as code. Each runs for ~{@link ALARM_PATTERN_MS} and is designed to
 * tile seamlessly, so the alarm loops by scheduling one pattern after another
 * rather than by gapping between short chimes.
 */

/** Every pattern fills this window, so repeats butt up against each other. */
export const ALARM_PATTERN_MS = 10_000;

/**
 * Peak gain for a single tone. Kept below 1 so stacked tones can't clip.
 *
 * Raised from 0.16: an alarm competing with plant noise (or just a laptop
 * speaker across a room) has to be genuinely loud, and the operator has a mute.
 */
const PEAK = 0.42;

/**
 * Alarm tones use a harmonically RICH waveform, not a pure sine.
 *
 * A sine puts all its energy at the fundamental, and small speakers roll off
 * hard below ~500Hz — a 350–440Hz sine at moderate gain is close to inaudible on
 * a laptop, which is exactly how the first Klaxon behaved. A triangle/sawtooth
 * spreads energy into harmonics the speaker CAN reproduce, so the same nominal
 * frequency actually gets heard. Every fundamental below also sits at 500Hz or
 * above for the same reason.
 */
const ALARM_WAVE: OscillatorType = "sawtooth";

/**
 * One shaped sine tone. `at`/`dur` are seconds on the context's own clock.
 * Connects to `out` rather than straight to the destination, so a caller can cut
 * the whole pattern by disconnecting that one node.
 */
const tone = (
  ctx: AudioContext,
  out: AudioNode,
  freq: number,
  at: number,
  dur: number,
  peak = PEAK,
  wave: OscillatorType = ALARM_WAVE,
): void => {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = wave;
  osc.frequency.setValueAtTime(freq, at);
  // Exponential ramps need a non-zero floor, hence 0.0001 rather than 0.
  gain.gain.setValueAtTime(0.0001, at);
  gain.gain.exponentialRampToValueAtTime(peak, at + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, at + dur);
  osc.connect(gain).connect(out);
  osc.start(at);
  osc.stop(at + dur + 0.02);
};

/** A tone that slides between two pitches — the basis of the siren. */
const sweep = (
  ctx: AudioContext,
  out: AudioNode,
  from: number,
  to: number,
  at: number,
  dur: number,
  peak = PEAK,
): void => {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = ALARM_WAVE;
  osc.frequency.setValueAtTime(from, at);
  osc.frequency.linearRampToValueAtTime(to, at + dur);
  gain.gain.setValueAtTime(0.0001, at);
  gain.gain.exponentialRampToValueAtTime(peak, at + 0.05);
  gain.gain.setValueAtTime(peak, at + dur - 0.08);
  gain.gain.exponentialRampToValueAtTime(0.0001, at + dur);
  osc.connect(gain).connect(out);
  osc.start(at);
  osc.stop(at + dur + 0.02);
};

export interface AlarmSound {
  id: string;
  /**
   * Translation keys for the picker's label and its one-line description. The
   * text is resolved at render so it follows a language change; the pattern
   * data below is language-independent and stays here.
   */
  labelKey: "alarmSounds.chime.label" | "alarmSounds.pulse.label" | "alarmSounds.siren.label" | "alarmSounds.klaxon.label";
  hintKey: "alarmSounds.chime.hint" | "alarmSounds.pulse.hint" | "alarmSounds.siren.hint" | "alarmSounds.klaxon.hint";
  /**
   * Schedules the whole ~10s pattern starting at `at` (context seconds), routed
   * through `out` so the caller can stop it mid-pattern.
   */
  schedule: (ctx: AudioContext, at: number, out: AudioNode) => void;
}

export const ALARM_SOUNDS: AlarmSound[] = [
  {
    id: "chime",
    labelKey: "alarmSounds.chime.label",
    hintKey: "alarmSounds.chime.hint",
    schedule: (ctx, at, out) => {
      // A5 → D6 pair every 1.25s: unobtrusive, for quiet control rooms.
      for (let i = 0; i < 8; i++) {
        const t = at + i * 1.25;
        // Sine on purpose: this is the deliberately soft option.
        tone(ctx, out, 880, t, 0.18, 0.3, "sine");
        tone(ctx, out, 1174.66, t + 0.12, 0.22, 0.3, "sine");
      }
    },
  },
  {
    id: "pulse",
    labelKey: "alarmSounds.pulse.label",
    hintKey: "alarmSounds.pulse.hint",
    schedule: (ctx, at, out) => {
      // Three quick beeps, then a rest: reads as "attention" without droning.
      for (let group = 0; group < 5; group++) {
        const base = at + group * 2;
        for (let i = 0; i < 3; i++) tone(ctx, out, 987.77, base + i * 0.22, 0.14);
      }
    },
  },
  {
    id: "siren",
    labelKey: "alarmSounds.siren.label",
    hintKey: "alarmSounds.siren.hint",
    schedule: (ctx, at, out) => {
      // Continuous up-sweeps, no silence: for noisy environments.
      for (let i = 0; i < 10; i++) sweep(ctx, out, 660, 1320, at + i, 1);
    },
  },
  {
    id: "klaxon",
    labelKey: "alarmSounds.klaxon.label",
    hintKey: "alarmSounds.klaxon.hint",
    schedule: (ctx, at, out) => {
      // Alternating low tones — carries through ambient plant noise.
      for (let i = 0; i < 10; i++) {
        const t = at + i;
        // Two-note alternation in the range small speakers actually reproduce —
        // the original 440/349Hz sine pair was effectively silent on a laptop.
        tone(ctx, out, 880, t, 0.42);
        tone(ctx, out, 659.25, t + 0.5, 0.42);
      }
    },
  },
];

export const DEFAULT_ALARM_SOUND_ID = "klaxon";

export const getAlarmSound = (id: string | undefined): AlarmSound =>
  ALARM_SOUNDS.find((s) => s.id === id) ??
  ALARM_SOUNDS.find((s) => s.id === DEFAULT_ALARM_SOUND_ID)!;
